import React, { useState, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import Dialog from '../../Dialog';
import { ColumnFlexBox, CenteredFlexBox } from '../../FlexBox';
import FetchAutoComplete from '../../Inputs/FetchAutoComplete';
import useAPI from '../../../api';
import useTranslate from '../../../Hooks/useTranslate';
import {
  CELL_TYPES,
  cellOptionFields,
  cellUsesOptions,
  cellUsesCaption,
  cellUsesToKey,
} from '../columns/CellRegistry';
import { entryKindLabel, entrySecondKey } from '../detail/EntryRegistry';
import {
  FILTER_TYPES,
  filterUsesOptions,
  filterUsesLookup,
  filterUsesScale,
  filterOptionFields,
} from '../filters/FilterRegistry';
import OptionsEditor from './OptionsEditor';
import SettingsFields, { KeyField } from './SettingsFields';
import {
  settingsFor,
  settingsIn,
  CONTENT,
  LAYOUT,
  VISIBILITY,
} from '../settings';
import SectionLabel from '../../SectionLabel';

const EMPTY_CAPTION = { key: '', label: '', options: [] };

/** What the entry's renderer is called, and what it reads of the entry. */
const renderer = (entry, surface) => {
  if (surface === 'filters') {
    const type = FILTER_TYPES.find(({ type: name }) => name === entry?.type);

    return {
      label: type?.label,
      hasOptions: filterUsesOptions(entry?.type),
      hasCaption: false,
      hasLookup: filterUsesLookup(entry?.type),
      // Only a slider has a scale to set; a date range takes its ends from the calendar.
      hasScale: filterUsesScale(entry?.type),
      optionFields: filterOptionFields(entry?.type),
      secondKey: null,
    };
  }

  return {
    // A kind drawing no value is named by what it is, since no cell draws it.
    label:
      CELL_TYPES.find(({ cell }) => cell === entry?.cell)?.label ??
      entryKindLabel(entry?.kind),
    hasOptions: cellUsesOptions(entry?.cell),
    hasCaption: cellUsesCaption(entry?.cell),
    hasLookup: false,
    hasScale: false,
    optionFields: cellOptionFields(entry?.cell),
    // A kind that reads two keys says which name the second goes under; a cell that does says only
    // that it does, and the name is the one the cells share.
    secondKey:
      entrySecondKey(entry?.kind) ??
      (cellUsesToKey(entry?.cell)
        ? { name: 'toKey', label: 'SECOND_COLUMN' }
        : null),
  };
};

/** The ends of a slider's scale, and what the entry calls each of them. */
const SCALE_BOUNDS = [
  {
    key: 'min',
    label: 'MIN',
    sourceKey: 'minSource',
    parameterKey: 'minParameter',
    pathKey: 'minPath',
  },
  {
    key: 'max',
    label: 'MAX',
    sourceKey: 'maxSource',
    parameterKey: 'maxParameter',
    pathKey: 'maxPath',
  },
];

const EMPTY_SOURCE = { root: '', url: '', parameters: [] };

/** The value a runtime-value picker holds when the procedure needs none. */
const NO_PARAMETERS = [];

/** A registry key as the picker shows it: the key itself, with what it is for beside it. */
const lookupKeyLabel = (option) => {
  if (!option) return '';
  if (typeof option === 'string') return option;

  return option.label ? `${option.id} - ${option.label}` : (option.id ?? '');
};

/**
 * Picks the registry key a lookup calls, out of the keys the registry holds.
 *
 * @param {Object} props - Component props.
 * @param {String} props.id - The field's id.
 * @param {String} props.value - The chosen key.
 * @param {Function} props.onChange - Receives the chosen key, empty for none.
 * @param {Function} props.fetchKeys - Answers with the keys matching what was typed.
 * @param {String} props.helperText - Shown under the field.
 *
 * @returns {React.JSX.Element}
 */
const LookupKeySelect = function LookupKeySelect({
  id,
  value = '',
  onChange = () => {},
  fetchKeys,
  helperText = '',
}) {
  const intl = useIntl();

  return (
    <FetchAutoComplete
      id={id}
      label={intl.messages.DATA_SOURCE}
      placeholder={intl.messages.SEARCH}
      size="small"
      allUpperCase
      noCustomValue
      selectedValue={value || null}
      getOptionLabel={lookupKeyLabel}
      fetchCallback={fetchKeys}
      onChangeSelectedValue={(_, chosen) =>
        onChange(chosen?.id ?? chosen ?? '')
      }
      helperText={helperText}
    />
  );
};

/** Where an end of the scale takes its number from. */
const BOUND_SOURCES = [
  { value: 'number', label: 'FIXED_NUMBER' },
  { value: 'parameter', label: 'RUNTIME_VALUE' },
  { value: 'lookup', label: 'LOOKUP' },
];

const EMPTY_SCALE = {
  min: '',
  max: '',
  step: '',
  unit: '',
  minSource: 'number',
  maxSource: 'number',
  minParameter: '',
  maxParameter: '',
  minPath: '',
  maxPath: '',
};

/** A scale field's value, as a number, dropping the ones left blank. */
const scaleNumber = (value) =>
  value === '' || value === null ? undefined : Number(value);

/**
 * Configures what a layout entry's renderer reads: the value map, the data source it fetches values
 * from, and - for a cell that renders one - the second line under the value.
 *
 * Only what the chosen cell or filter type reads is offered. Edits are held here and handed over on
 * confirm.
 *
 * @param {Object} props - Component props.
 * @param {Boolean} props.open - Whether the dialog is open.
 * @param {Function} props.close - Closes it, discarding the edits.
 * @param {Object} props.entry - The layout entry being configured.
 * @param {String} props.surface - The surface the entry belongs to.
 * @param {Array} props.catalogue - The keys the section can show, for the second line.
 * @param {Array} props.parameters - Runtime values the page publishes, nameable by a scale bound.
 * @param {Function} props.onConfirm - Receives the changes to the entry.
 *
 * @returns {React.JSX.Element}
 */
const EntryDialog = function EntryDialog({
  open = false,
  close = () => {},
  entry = null,
  surface = 'list',
  catalogue = [],
  parameters = [],
  siblings = [],
  onConfirm = () => {},
}) {
  const intl = useIntl();
  const translate = useTranslate();
  const API = useAPI();

  const [scale, setScale] = useState(EMPTY_SCALE);
  const [sources, setSources] = useState([]);
  const [options, setOptions] = useState([]);
  const [caption, setCaption] = useState(EMPTY_CAPTION);
  const [optionsUrl, setOptionsUrl] = useState('');
  const [secondKeyValue, setSecondKeyValue] = useState('');
  const [settings, setSettings] = useState({});

  // The entry as the edits leave it, which is what the controls show and what decides which of them
  // are offered at all: a setting that appears only once another is turned on has to appear as it is
  // turned on, rather than once the dialog has been confirmed and opened again.
  const edited = useMemo(
    () => ({ ...(entry ?? {}), ...settings }),
    [entry, settings]
  );

  // What the entry's own type declares it reads, split into what it shows and how it is shown.
  const offered = useMemo(
    () => settingsFor(edited, surface),
    [edited, surface]
  );
  const content = useMemo(() => settingsIn(offered, CONTENT), [offered]);
  const layout = useMemo(() => settingsIn(offered, LAYOUT), [offered]);
  const visibility = useMemo(() => settingsIn(offered, VISIBILITY), [offered]);

  useEffect(() => {
    if (!open) return;

    // The edits alone, so a control shows the entry with them laid over it. Seeded from the entry it
    // would miss a setting not offered yet - the size of a marker that is not one until the switch
    // beside it is turned on.
    setSettings({});
    setOptions(entry?.options ?? []);
    setCaption({ ...EMPTY_CAPTION, ...(entry?.caption ?? {}) });
    setSecondKeyValue(
      entry?.[entrySecondKey(entry?.kind)?.name ?? 'toKey'] ?? ''
    );
    setOptionsUrl(entry?.optionsUrl ?? '');
    setSources(entry?.sources ?? []);
    setScale({
      ...EMPTY_SCALE,
      // An entry saved before the sources were declared says so by naming a runtime value.
      minSource: entry?.minParameter ? 'parameter' : 'number',
      maxSource: entry?.maxParameter ? 'parameter' : 'number',
      ...Object.fromEntries(
        Object.keys(EMPTY_SCALE)
          .filter(
            (name) => entry?.[name] !== undefined && entry?.[name] !== null
          )
          .map((name) => [name, entry[name]])
      ),
    });
  }, [open, entry, surface]);

  const {
    label: rendererLabel,
    hasOptions,
    hasCaption,
    hasLookup,
    hasScale,
    optionFields,
    secondKey,
  } = renderer(entry, surface);

  const named = sources.filter(({ root, url }) => root && url);
  // A root twice over would leave one of the two answers unreachable.
  const duplicateRoot = (root, index) =>
    Boolean(root) &&
    sources.findIndex((source) => source.root === root) !== index;

  const updateSource = (index, changes) =>
    setSources((current) =>
      current.map((source, position) =>
        position === index ? { ...source, ...changes } : source
      )
    );

  const updateScale = (name, value) =>
    setScale((current) => ({ ...current, [name]: value }));

  const fetchLookupKeys = (typed, onSuccess) =>
    API.lookupKeys(typed, (res) => onSuccess(res ?? []));

  /** What a runtime value is called, where only its name is at hand. */
  const parameterLabel = (name) =>
    translate(
      parameters.find((parameter) => parameter.name === name)?.label ?? name
    );

  /** The runtime values the page publishes, as the entries a picker offers. */
  const parameterItems = parameters.map(({ name }) => (
    <MenuItem key={name} value={name}>
      {parameterLabel(name)}
    </MenuItem>
  ));

  return (
    <Dialog
      id="entry-dialog"
      title={`${translate(rendererLabel)} — ${translate(
        entry?.label ?? entry?.key
      )}`}
      open={open}
      onClose={close}
      onSubmit={() => {
        onConfirm({
          ...settings,
          ...(hasOptions ? { options } : {}),
          ...(secondKey
            ? { [secondKey.name]: secondKeyValue || undefined }
            : {}),
          ...(hasCaption ? { caption: caption.key ? caption : undefined } : {}),
          ...(hasLookup ? { optionsUrl: optionsUrl || undefined } : {}),
          ...(hasScale
            ? {
                step: scaleNumber(scale.step),
                unit: scale.unit || undefined,
                // The lookups are declared once for the entry; a path is what reaches into them.
                sources: named.length > 0 ? named : undefined,
                // An end carries only what its own source needs.
                ...Object.fromEntries(
                  SCALE_BOUNDS.flatMap(
                    ({ key, sourceKey, parameterKey, pathKey }) => {
                      const source = scale[sourceKey];

                      return [
                        [sourceKey, source],
                        [
                          key,
                          source === 'number'
                            ? scaleNumber(scale[key])
                            : undefined,
                        ],
                        [
                          parameterKey,
                          source === 'parameter'
                            ? scale[parameterKey] || undefined
                            : undefined,
                        ],
                        [
                          pathKey,
                          source === 'lookup'
                            ? scale[pathKey] || undefined
                            : undefined,
                        ],
                      ];
                    }
                  )
                ),
              }
            : {}),
        });
        close();
      }}
      submitLabel="CONFIRM"
      width="md"
      fullWidth
      dividers
    >
      <ColumnFlexBox gap="16px" sx={{ flex: 1, minWidth: 0 }}>
        {/* What is shown: the wording, and whatever the chosen type reads of the value. */}
        {content.length > 0 && (
          <>
            <SectionLabel>{intl.messages.SETTINGS_CONTENT}</SectionLabel>
            <SettingsFields
              settings={content}
              values={edited}
              catalogue={catalogue}
              onChange={(changes) =>
                setSettings((current) => ({ ...current, ...changes }))
              }
            />
          </>
        )}
        {hasScale && (
          <>
            <SectionLabel>{intl.messages.SOURCES}</SectionLabel>
            {sources.map((source, index) => (
              <CenteredFlexBox key={`source-${String(index)}`} gap="12px">
                {/* The name the answer is filed under, and the first step of every path into it. */}
                <TextField
                  size="small"
                  label={intl.messages.ROOT}
                  value={source.root ?? ''}
                  onChange={(e) =>
                    updateSource(index, { root: e.target.value })
                  }
                  error={duplicateRoot(source.root, index)}
                  sx={{ width: '140px', flex: 'none' }}
                />
                {/* The registry says which keys exist, so one cannot be typed wrong. */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <LookupKeySelect
                    id={`source-url-${String(index)}`}
                    value={source.url}
                    onChange={(key) => updateSource(index, { url: key })}
                    fetchKeys={fetchLookupKeys}
                  />
                </Box>
                <Select
                  multiple
                  size="small"
                  displayEmpty
                  value={source.parameters ?? []}
                  onChange={(e) => {
                    const chosen =
                      typeof e.target.value === 'string'
                        ? e.target.value.split(',')
                        : e.target.value;

                    // Picking "none" is a choice of its own, so it clears the rest.
                    updateSource(index, {
                      parameters: chosen.includes('')
                        ? NO_PARAMETERS
                        : chosen.filter(Boolean),
                    });
                  }}
                  renderValue={(chosen) =>
                    chosen.length === 0
                      ? intl.messages.NO_PARAMETERS_NEEDED
                      : chosen.map(parameterLabel).join(', ')
                  }
                  sx={{ width: '240px', flex: 'none' }}
                >
                  <MenuItem value="">
                    {intl.messages.NO_PARAMETERS_NEEDED}
                  </MenuItem>
                  {parameterItems}
                </Select>
                <IconButton
                  id={`remove-source-${String(index)}`}
                  size="small"
                  onClick={() =>
                    setSources((current) =>
                      current.filter((_, position) => position !== index)
                    )
                  }
                >
                  <DeleteRoundedIcon fontSize="small" />
                </IconButton>
              </CenteredFlexBox>
            ))}
            <Button
              id="add-source-btn"
              size="small"
              startIcon={<AddRoundedIcon />}
              onClick={() =>
                setSources((current) => [...current, { ...EMPTY_SOURCE }])
              }
              sx={{ alignSelf: 'flex-start' }}
            >
              {translate('ADD_SOURCE')}
            </Button>
            <Divider />
            <SectionLabel>{intl.messages.SCALE}</SectionLabel>
            {SCALE_BOUNDS.map(
              ({ key, label, sourceKey, parameterKey, pathKey }) => (
                <CenteredFlexBox key={key} gap="12px">
                  <Typography
                    variant="body2"
                    sx={{ width: '48px', flex: 'none' }}
                  >
                    {intl.messages[label]}
                  </Typography>
                  <Select
                    size="small"
                    value={scale[sourceKey]}
                    onChange={(e) => updateScale(sourceKey, e.target.value)}
                    sx={{ width: '180px', flex: 'none' }}
                  >
                    {BOUND_SOURCES.map(({ value, label: sourceLabel }) => (
                      <MenuItem
                        key={value}
                        value={value}
                        // Nothing to read from until a source has been declared.
                        disabled={value === 'lookup' && named.length === 0}
                      >
                        {translate(sourceLabel)}
                      </MenuItem>
                    ))}
                  </Select>
                  {scale[sourceKey] === 'parameter' && (
                    <Select
                      size="small"
                      displayEmpty
                      value={scale[parameterKey]}
                      onChange={(e) =>
                        updateScale(parameterKey, e.target.value)
                      }
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      <MenuItem value="">{intl.messages.NONE}</MenuItem>
                      {parameterItems}
                    </Select>
                  )}
                  {scale[sourceKey] === 'number' && (
                    <TextField
                      size="small"
                      type="number"
                      label={intl.messages.FIXED_NUMBER}
                      value={scale[key]}
                      onChange={(e) => updateScale(key, e.target.value)}
                      sx={{ flex: 1, minWidth: 0 }}
                    />
                  )}
                  {/* Where in the merged answers this end reads: a root, then a dotted path. */}
                  {scale[sourceKey] === 'lookup' && (
                    <TextField
                      size="small"
                      label={intl.messages.PATH}
                      placeholder={`${named[0]?.root ?? intl.messages.SOURCE}.${key}`}
                      value={scale[pathKey]}
                      onChange={(e) => updateScale(pathKey, e.target.value)}
                      sx={{ flex: 1, minWidth: 0 }}
                    />
                  )}
                </CenteredFlexBox>
              )
            )}
            <CenteredFlexBox gap="12px">
              <TextField
                size="small"
                type="number"
                label={intl.messages.STEP}
                value={scale.step}
                onChange={(e) => updateScale('step', e.target.value)}
                sx={{ width: '160px', flex: 'none' }}
              />
              <TextField
                size="small"
                label={intl.messages.UNIT}
                value={scale.unit}
                onChange={(e) => updateScale('unit', e.target.value)}
                sx={{ width: '160px', flex: 'none' }}
              />
            </CenteredFlexBox>
          </>
        )}
        {secondKey && (
          <>
            <SectionLabel>{translate(secondKey.label)}</SectionLabel>
            <KeyField
              label={intl.messages.COLUMN}
              value={secondKeyValue}
              catalogue={catalogue}
              onChange={setSecondKeyValue}
            />
            <Divider />
          </>
        )}
        {hasScale && (hasOptions || hasLookup) && <Divider />}
        {hasLookup && (
          <>
            <SectionLabel>{intl.messages.DATA_SOURCE}</SectionLabel>
            <LookupKeySelect
              id="options-url"
              value={optionsUrl}
              onChange={setOptionsUrl}
              fetchKeys={fetchLookupKeys}
              helperText={
                entry?.contextParameters?.length > 0
                  ? `${intl.messages.PARAMETERS}: ${entry.contextParameters
                      .map(parameterLabel)
                      .join(', ')}`
                  : ''
              }
            />
          </>
        )}
        {hasOptions && (
          <>
            <CenteredFlexBox gap="8px">
              <SectionLabel>{intl.messages.VALUE_MAP}</SectionLabel>
              <Typography variant="caption" sx={{ opacity: '60%' }}>
                {entry?.key}
              </Typography>
            </CenteredFlexBox>
            <OptionsEditor
              options={options}
              onChange={setOptions}
              fields={optionFields}
              valueKey={entry?.key}
            />
          </>
        )}
        {hasOptions && hasCaption && <Divider />}
        {hasCaption && (
          <>
            <CenteredFlexBox gap="8px">
              <SectionLabel>{intl.messages.SECOND_LINE}</SectionLabel>
              <Button
                id="clear-caption-btn"
                size="small"
                color="inherit"
                disabled={!caption.key}
                onClick={() => setCaption(EMPTY_CAPTION)}
              >
                {intl.messages.CLEAR}
              </Button>
            </CenteredFlexBox>
            <CenteredFlexBox gap="12px">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <KeyField
                  label={intl.messages.COLUMN}
                  value={caption.key}
                  catalogue={catalogue}
                  onChange={(key) =>
                    setCaption((current) => ({
                      ...current,
                      key,
                      label:
                        current.label ||
                        catalogue.find((known) => known.key === key)?.label ||
                        '',
                    }))
                  }
                />
              </Box>
              <TextField
                size="small"
                label={intl.messages.LABEL}
                value={caption.label ?? ''}
                onChange={(e) =>
                  setCaption((current) => ({
                    ...current,
                    label: e.target.value,
                  }))
                }
                sx={{ width: '240px', flex: 'none' }}
              />
            </CenteredFlexBox>
            {/* The second line reads the value's label, and nothing else of the value map. */}
            {Boolean(caption.key) && (
              <OptionsEditor
                options={caption.options ?? []}
                onChange={(entries) =>
                  setCaption((current) => ({ ...current, options: entries }))
                }
                fields={['label']}
                valueKey={caption.key}
              />
            )}
          </>
        )}

        {/* How it is shown: where it sits and how large it reads. Kept apart
            from the content because they are answered at different moments - what a page says, and
            then how it looks saying it. */}
        {layout.length > 0 && (
          <>
            <Divider />
            <SectionLabel>{intl.messages.SETTINGS_LAYOUT}</SectionLabel>
          </>
        )}
        {layout.length > 0 && (
          <SettingsFields
            settings={layout}
            values={edited}
            catalogue={catalogue}
            onChange={(changes) =>
              setSettings((current) => ({ ...current, ...changes }))
            }
          />
        )}
        {/* When it is shown, and what it reads to decide - kept apart from both the content and the
            arrangement, which are about the entry itself rather than about the state around it. */}
        {visibility.length > 0 && (
          <>
            <Divider />
            <SectionLabel>{intl.messages.SETTINGS_VISIBILITY}</SectionLabel>
            <SettingsFields
              settings={visibility}
              values={edited}
              catalogue={catalogue}
              siblings={siblings}
              parameters={parameters}
              surface={surface}
              onChange={(changes) =>
                setSettings((current) => ({ ...current, ...changes }))
              }
            />
          </>
        )}
      </ColumnFlexBox>
    </Dialog>
  );
};

export default EntryDialog;
